// Why `indexReadyFor` refused — the typed rejection the navigational
// lifecycle hands its callers.
//
// A rejected index wait has two very different meanings for the surface that
// was waiting, and it cannot tell them apart from a message:
//
//   - `no-lifecycle` — this connection has NO index and never will right now:
//     the mock engine (the lifecycle skips it), an engine the app does not
//     index, or a connection that is no longer in the store. Nothing was
//     searched because there is nothing to search. The honest answer is the
//     empty result.
//   - `policy-unknown` — the connection is live, but its case policy could not
//     be learnt, so no index was created rather than one keyed on a guess.
//   - `build-failed` — the index WAS created (its case policy is known) but the
//     catalog read behind it rejected: a failed IPC call, a dropped
//     introspection query, an engine error mid-paging. Whatever the builder had
//     already recorded stays in the index and the index is marked PARTIAL, so a
//     reader that consults it anyway knows it is not the whole catalog — but
//     this wait searched an index that never finished, so it must not fulfil.
//     The next request REBUILDS into the same index rather than re-reporting
//     the failure forever (`navLifecycleBuild.ts` → `runBuild`).
//   - `cancelled` — this wait's own build or probe was abandoned while the
//     wait was pending. Two shapes: an index WAS building and a teardown
//     cancelled it (the promise still settles — the builder returns at its
//     next slice check — and rejects with this reason whether or not a
//     reconnect followed); or only the case-policy probe was in flight and a
//     reconnect under the same id REPLACED the lifecycle entry before it
//     landed (a plain teardown during the probe, with nothing replacing the
//     entry, is `no-lifecycle`). Either way, fulfilling the ORIGINAL wait would
//     hand the waiter the result of an index that was cleared, or the new
//     lifecycle's absent or not-yet-built one — never a completed search. The
//     surface tells a gone connection from a replaced one by the connection's
//     status: gone → the empty result is honest; still connected → an error.
//
// The last three searched NOTHING (or not everything) on a connection the user
// can still see, and reporting that as "no references found" is the false
// negative the find-references surfaces exist to avoid — so they show an error
// state and invite a retry.
//
// A TAGGED error object, not a class (CLAUDE.md's no-classes rule). The tag is
// what the guard reads, so the rejection survives a module loaded twice under
// two graphs (Vitest's mock registry, the packaged bundle) — where `instanceof`
// silently stops matching.

export type NavIndexUnavailableReason =
  | 'no-lifecycle'
  | 'policy-unknown'
  | 'cancelled'
  | 'build-failed';

export type NavIndexUnavailableError = Error & {
  readonly name: 'NavIndexUnavailableError';
  readonly reason: NavIndexUnavailableReason;
};

export function navIndexUnavailable(
  reason: NavIndexUnavailableReason,
  message: string,
): NavIndexUnavailableError {
  const error = new Error(message) as Error & {
    name: 'NavIndexUnavailableError';
    reason: NavIndexUnavailableReason;
  };
  error.name = 'NavIndexUnavailableError';
  error.reason = reason;
  return error;
}

export function isNavIndexUnavailable(err: unknown): err is NavIndexUnavailableError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { name?: unknown }).name === 'NavIndexUnavailableError'
  );
}

/** Keep an already-typed rejection as it is; tag anything else with `reason`.
 *  Used where a dependency (a case-policy probe, say) may reject with an error
 *  of its own — the caller still has to be able to classify it. */
export function asNavIndexUnavailable(
  reason: NavIndexUnavailableReason,
  err: unknown,
): NavIndexUnavailableError {
  if (isNavIndexUnavailable(err)) return err;
  return navIndexUnavailable(reason, err instanceof Error ? err.message : String(err));
}
